import type { BreadcrumbItem } from 'components/uiKit/Breadcrumbs/Breadcrumbs'
import { ENUM_VALUE_MAP, valueToEnum } from 'components/uiKit/SortPopover/constants'
import { SortUiEnum } from 'components/uiKit/SortPopover/types'
import type {
  EmployeePreferencesByProjectIdQuery,
  EmployeePreferencesSchemaFragment,
} from 'gql/__generated__/graphql'
import { EmployeePreferencesTab } from 'gql/__generated__/graphql'
import { BlockMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { getEnumLabel, getEnumOption } from 'utils/enum'

import { TemplateStepperState } from './types'

type OrderType = NonNullable<EmployeePreferencesSchemaFragment['templates']>['order']

export const TABS: { value: EmployeePreferencesTab; label: string }[] = [
  getEnumOption('EmployeePreferencesTab', EmployeePreferencesTab.collections),
  getEnumOption('EmployeePreferencesTab', EmployeePreferencesTab.recently_used),
  getEnumOption('EmployeePreferencesTab', EmployeePreferencesTab.favorites),
]

export const singleBlockModes = [BlockMode.start, BlockMode.end, BlockMode.cover]

export function getTemplateStepperState({
  completed,
  disabled,
  alert,
  active,
}: {
  completed: boolean
  disabled: boolean
  alert: boolean
  active: boolean
}) {
  const statusArrays: [TemplateStepperState, boolean][] = [
    [TemplateStepperState.completed, completed],
    [TemplateStepperState.disabled, disabled],
    [TemplateStepperState.alert, alert],
    [TemplateStepperState.active, active],
    [TemplateStepperState.notSelected, true],
  ]
  return statusArrays.find(([_key, value]) => value)?.[0] || TemplateStepperState.notSelected
}

export const generateBreadcrumbItems = (
  search?: string,
  activeCollection?: { id: string; name: string } | null,
  tab?: EmployeePreferencesTab,
): BreadcrumbItem[] => {
  const baseBreadcrumbItems: BreadcrumbItem[] = tab
    ? [{ name: getEnumLabel('EmployeePreferencesTab', tab), id: tab }]
    : [{ name: t('page.templates.title'), id: 'templates' }]

  if (activeCollection && tab === EmployeePreferencesTab.collections) {
    baseBreadcrumbItems.push({ id: activeCollection.id, name: activeCollection.name })
  }

  if (search) {
    baseBreadcrumbItems.push({ name: t('uiKit.breadcrumb.search'), id: 'search' })
  }

  return baseBreadcrumbItems
}

export const SORT_OPTIONS = [
  getEnumOption('SortUiEnum', SortUiEnum.NAME_ASC),
  getEnumOption('SortUiEnum', SortUiEnum.NAME_DESC),
]

export const DEFAULT_SORT_TAG = [ENUM_VALUE_MAP.NAME_ASC[0][0], ENUM_VALUE_MAP.NAME_ASC[0][1]]

export const SORT_NAME_TAG = 'collection'

export const employeePreferencesToOrder = (order: OrderType) =>
  order ? valueToEnum(order.field, order.value) : SortUiEnum.NAME_ASC

export const SINGLE_MODE: BlockMode[] = [BlockMode.cover, BlockMode.end, BlockMode.start]

export const COUNT_LIMIT = 15
export const HORIZONTAL_GAP = 10
export const VERTICAL_GAP = 10
export const TEMPLATE_MIN_WIDTH = 298
export const SCALE_INITIAL = 0.9
export const SCALE_FINAL = 1
export const OPACITY_INITIAL = 0
export const OPACITY_FINAL = 1
export const TRANSITION_DURATION = 0.6
export const TRANSITION_EASE = [0.42, 0, 0.58, 1]
export const FAKE_SIZE = 100

export const getDefaultTemplateValue = (
  employeePreferencesData?: EmployeePreferencesByProjectIdQuery,
  projectId?: string,
) => {
  const preferencesTemplates = employeePreferencesData?.data?.employeePreferences?.templates
  const { tab, editorTemplateGroupId, editorTemplateGroupName } = preferencesTemplates || {}
  const defTab = projectId
    ? tab || EmployeePreferencesTab.recently_used
    : EmployeePreferencesTab.collections
  const templatesType = preferencesTemplates?.templatesType
  const defTag = {
    ...templatesType,
    position: 0,
    id: templatesType?.id || null,
    name: templatesType?.name || '',
  }
  const defCollection = editorTemplateGroupId
    ? { id: editorTemplateGroupId || '', name: editorTemplateGroupName || '' }
    : null

  return { defTab, defTag, defCollection, preferencesTemplates }
}
